import { createClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const configError =
  !url || !anonKey || url.includes('your-project-ref')
    ? 'Supabase is not configured. Copy .env.example to .env and fill in VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY, then restart `npm run dev`.'
    : null;

export const supabase = createClient(url || 'http://localhost', anonKey || 'anon', {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

/**
 * The whole reason the Log screen looked "mysteriously broken": every query in
 * the old code was written as `const { data } = await supabase...`, which drops
 * the error on the floor. A missing column then reads as an empty screen rather
 * than an error message.
 *
 * Nothing in this app calls Supabase without going through `q`. If a query
 * fails, it throws — loudly, with the Postgres code attached — and the screen
 * shows a banner instead of pretending it has no data.
 */
export class DbError extends Error {
  constructor(context, error) {
    const code = error?.code ? ` [${error.code}]` : '';
    super(`${context}: ${error?.message || 'unknown error'}${code}`);
    this.name = 'DbError';
    this.context = context;
    this.code = error?.code;
    this.hint = error?.hint;
    this.details = error?.details;
  }
}

export async function q(builder, context) {
  const { data, error } = await builder;
  if (error) throw new DbError(context, error);
  return data;
}

/** Turns a raw Postgres failure into something a human can act on. */
export function explain(err) {
  if (!(err instanceof DbError)) return err?.message || String(err);
  switch (err.code) {
    case '42703':
      return `${err.message}\n\nA column the app expects is missing from your database. Run sql/fix-schema.sql in the Supabase SQL Editor.`;
    case '42P10':
      return `${err.message}\n\nThe unique constraint on game_participants(game_id, member_id) is missing. Run sql/fix-schema.sql.`;
    case '42P01':
      return `${err.message}\n\nA table or view is missing. Run sql/schema.sql, then sql/fix-schema.sql.`;
    case '42501':
    case 'PGRST301':
      // The commonest way an ordinary member hits this is a deadline, not a
      // misconfiguration. Saying "check your RLS policies" to someone who just
      // tried to vote on a four-day-old game is an answer to the wrong question.
      if (/rating|vote/i.test(err.context || '')) {
        return 'Voting on that game has closed. Ratings and man-of-the-match votes are open for 72 hours after a game is logged.';
      }
      return `${err.message}\n\nRow Level Security blocked this. Check the policies in sql/rls.sql.`;
    case '23505':
      if (/rating/i.test(err.context || '')) {
        return 'You have already rated that player for this game, and ratings are final.';
      }
      return `${err.message}\n\nThat record already exists.`;
    case '23514':
      // The single-pass trigger raises this. Its own message is already written
      // for a person, so pass it through rather than wrapping it in jargon.
      if (/already submitted/i.test(err.message || '')) return err.message.replace(/^.*?: /, '');
      return `${err.message}\n\nThat value is outside the range the database allows.`;
    case '23502':
      return `${err.message}\n\nA required column was sent as empty. If this happened while logging a result, run sql/09-ten-point-scale.sql.`;
    default:
      return err.message;
  }
}
